"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _tinycolor = require("@ctrl/tinycolor");
var _Select = require("../../components/Select");
var _getRgbaFromHex = _interopRequireDefault(require("../../utils/getRgbaFromHex"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * @copyright
 * Copyright (c) 2022 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */

var getMainColor = function getMainColor(_ref) {
  var props = _ref.props,
    theme = _ref.theme;
  var darkenWith = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  return new _tinycolor.TinyColor(props.errorText ? theme.colors.alert : theme.colors.primary).darken(darkenWith).toHexString();
};
var getBorderColor = function getBorderColor(_ref2) {
  var state = _ref2.state,
    props = _ref2.props,
    theme = _ref2.theme,
    _ref2$mainColor = _ref2.mainColor,
    mainColor = _ref2$mainColor === void 0 ? theme.colors.baseContrast040 : _ref2$mainColor;
  if (props.errorText) {
    return theme.colors.alert;
  }
  if (props.button === "flat") {
    return "transparent";
  }
  if (state.isFocused) {
    return theme.colors.primary;
  }
  return mainColor;
};
var getFocusedOnly = function getFocusedOnly(_ref3) {
  var state = _ref3.state,
    theme = _ref3.theme;
  if (state.isFocused) {
    return (0, _getRgbaFromHex.default)(theme.colors.primary, 0.1);
  }
  return "unset";
};
var getFocusedWithError = function getFocusedWithError(_ref4) {
  var props = _ref4.props,
    state = _ref4.state,
    theme = _ref4.theme;
  if (props.errorText && state.isFocused) {
    return (0, _getRgbaFromHex.default)(theme.colors.alert, 0.1);
  }
  return getFocusedOnly({
    state: state,
    theme: theme
  });
};
var getOptionBackgroundColor = function getOptionBackgroundColor(_ref5) {
  var state = _ref5.state,
    theme = _ref5.theme;
  if (state.isSelected) {
    return theme.colors.primary;
  }
  return getFocusedOnly({
    state: state,
    theme: theme
  });
};
var getPlaceholderStyles = function getPlaceholderStyles(_ref6) {
  var state = _ref6.state,
    theme = _ref6.theme;
  if (state.isDisabled) {
    return theme.colors.baseContrast030;
  }
  return theme.colors.baseContrast050;
};
var getSingleValueStyles = function getSingleValueStyles(_ref7) {
  var props = _ref7.props,
    state = _ref7.state,
    theme = _ref7.theme;
  if (state.isDisabled) {
    return theme.colors.baseContrast030;
  }
  if (props.button !== null) {
    return theme.colors.baseContrast100;
  }
  return theme.colors.baseContrast070;
};
var Select = {
  styles: function styles(theme) {
    return {
      labelText: {
        color: theme.colors.baseContrast070,
        fontSize: _Select.selectVars.get("labelFontSize", "".concat(theme.typography.fontSize, "px")),
        fontWeight: 600,
        textTransform: "capitalize",
        marginBottom: 2
      }
    };
  },
  select: {
    control: function control(_ref8) {
      var state = _ref8.state,
        props = _ref8.props,
        theme = _ref8.theme;
      return {
        borderColor: getBorderColor({
          state: state,
          props: props,
          theme: theme
        }),
        borderWidth: 2,
        borderRadius: 2,
        color: theme.colors.baseContrast070,
        paddingInlineStart: theme.spacing.base * 1.5 - 1,
        background: getFocusedWithError({
          props: props,
          state: state,
          theme: theme
        }),
        "&:hover": {
          background: getFocusedWithError({
            props: props,
            state: state,
            theme: theme
          }),
          borderColor: getBorderColor({
            state: state,
            props: props,
            theme: theme,
            mainColor: theme.colors.primary
          }),
          "& div > div[id*='placeholder']": {
            color: getMainColor({
              props: props,
              theme: theme
            })
          }
        }
      };
    },
    placeholder: function placeholder(_ref9) {
      var state = _ref9.state,
        theme = _ref9.theme;
      return {
        color: getPlaceholderStyles({
          state: state,
          theme: theme
        })
      };
    },
    singleValue: function singleValue(_ref0) {
      var state = _ref0.state,
        props = _ref0.props,
        theme = _ref0.theme;
      return {
        color: getSingleValueStyles({
          state: state,
          props: props,
          theme: theme
        })
      };
    },
    dropdownIndicator: function dropdownIndicator(_ref1) {
      var state = _ref1.state,
        theme = _ref1.theme;
      return {
        "& svg": {
          fill: state.isDisabled ? theme.colors.baseContrast030 : theme.colors.baseContrast070
        }
      };
    },
    menu: function menu(_ref10) {
      var theme = _ref10.theme;
      return {
        borderColor: theme.colors.primary,
        borderWidth: 2
      };
    },
    multiValueRemove: function multiValueRemove(_ref11) {
      var props = _ref11.props,
        state = _ref11.state,
        theme = _ref11.theme;
      return {
        color: theme.colors.themeBase,
        background: state.isDisabled ? theme.colors.baseContrast030 : getMainColor({
          props: props,
          theme: theme
        }),
        borderRadius: 0,
        borderStartEndRadius: 2,
        borderEndEndRadius: 2,
        "&:hover": {
          background: getMainColor({
            props: props,
            theme: theme
          }, 10),
          color: theme.colors.themeBase
        }
      };
    },
    multiValue: function multiValue(_ref12) {
      var props = _ref12.props,
        state = _ref12.state,
        theme = _ref12.theme;
      return {
        background: state.isDisabled ? theme.colors.baseContrast030 : getMainColor({
          props: props,
          theme: theme
        })
      };
    },
    multiValueLabel: function multiValueLabel(_ref13) {
      var theme = _ref13.theme;
      return {
        color: theme.colors.themeBase,
        fontWeight: "bold"
      };
    },
    option: function option(_ref14) {
      var state = _ref14.state,
        theme = _ref14.theme;
      return {
        backgroundColor: getOptionBackgroundColor({
          state: state,
          theme: theme
        })
      };
    },
    group: function group(_ref15) {
      var theme = _ref15.theme;
      return {
        fontSize: theme.typography.fontSize,
        color: theme.colors.baseContrast060
      };
    },
    groupHeading: function groupHeading(_ref16) {
      var theme = _ref16.theme;
      return {
        fontSize: theme.typography.fontSize - 3,
        color: theme.colors.primary,
        fontWeight: "bold"
      };
    },
    clearIndicator: function clearIndicator(_ref17) {
      var props = _ref17.props,
        theme = _ref17.theme;
      return {
        "& svg": {
          width: theme.spacing.base * 3,
          height: theme.spacing.base * 3,
          fill: getMainColor({
            props: props,
            theme: theme
          }),
          "&:hover": {
            fill: getMainColor({
              props: props,
              theme: theme
            }, 10)
          }
        }
      };
    }
  }
};
var _default = exports.default = Select;